import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchApprovedTemplates, createBroadcast, updateBroadcast } from '../store/slices/broadcastsSlice';
import { fetchBroadcastLists } from '../store/slices/broadcastListsSlice';
import { fetchCustomers } from '../store/slices/customersSlice';
import './CreateBroadcast.css';

const CUSTOMER_FETCH_LIMIT = 200;

/**
 * Extract {{N}} variable indices from a template text string
 * Returns sorted array of variable numbers found, e.g. [1, 2, 3]
 */
const extractVarNumbers = (text = '') => {
  const matches = text.match(/\{\{(\d+)\}\}/g) || [];
  const nums = matches.map(m => parseInt(m.replace(/[{}]/g, '')));
  return [...new Set(nums)].sort((a, b) => a - b);
};

/**
 * Derive template variables from DB template components
 * Returns array of { key, label, componentType, varNum } objects
 */
const deriveTemplateVariables = (template) => {
  if (!template) return [];
  const vars = [];

  const headerText = template.components?.header?.text || '';
  extractVarNumbers(headerText).forEach(n => {
    vars.push({ key: `header_${n}`, label: `Header Variable ${n}`, componentType: 'header', varNum: n });
  });

  const bodyText = template.components?.body?.text || '';
  extractVarNumbers(bodyText).forEach(n => {
    vars.push({ key: `body_${n}`, label: `Body Variable ${n}`, componentType: 'body', varNum: n });
  });

  (template.components?.buttons || []).forEach((btn, btnIdx) => {
    if (btn.url) {
      extractVarNumbers(btn.url).forEach(n => {
        vars.push({ key: `button_${btnIdx}_${n}`, label: `Button "${btn.text}" Variable ${n}`, componentType: 'button', varNum: n });
      });
    }
  });

  // Fallback: use stored variables array if no components found
  if (vars.length === 0 && template.variables && template.variables.length > 0) {
    template.variables.forEach((v, i) => {
      vars.push({ key: `var_${i}`, label: v.name || `Variable ${i + 1}`, componentType: 'body', varNum: i + 1 });
    });
  }

  return vars;
};

/**
 * Build template body preview with variables substituted
 */
const buildPreview = (text = '', vars, varValues) => {
  let preview = text;
  vars.forEach(v => {
    const value = varValues[v.key] || `[${v.label}]`;
    preview = preview.replace(new RegExp(`\\{\\{${v.varNum}\\}\\}`, 'g'), value);
  });
  return preview;
};

const normalizeRecipient = (recipient) => {
  if (!recipient) return null;

  if (typeof recipient === 'string') {
    return { phoneNumber: recipient, customerName: null };
  }

  const phoneNumber = recipient.phoneNumber || recipient.phone;
  const customerName = recipient.customerName || recipient.name || null;

  if (!phoneNumber) return null;

  return { phoneNumber, customerName };
};

const extractCustomersFromPayload = (payload) => {
  if (Array.isArray(payload)) {
    if (payload.length === 1 && Array.isArray(payload[0]?.data)) return payload[0].data;
    return payload;
  }

  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.customers)) return payload.customers;
  if (Array.isArray(payload?.results)) return payload.results;

  return [];
};

const extractPaginationFromPayload = (payload) => {
  if (payload?.pagination) return payload.pagination;
  if (payload?.data?.pagination) return payload.data.pagination;
  if (Array.isArray(payload) && payload.length === 1 && payload[0]?.pagination) return payload[0].pagination;
  return {};
};

const ALL_STEP_IDS = ['name', 'template', 'variables', 'recipients', 'schedule'];
const STEP_META = {
  name:       { label: 'Broadcast Name',     desc: 'Give your broadcast an internal name' },
  template:   { label: 'Select Template',    desc: 'Pick an approved WhatsApp template' },
  variables:  { label: 'Template Variables', desc: 'Set values for template placeholders' },
  recipients: { label: 'Recipients',         desc: 'Choose who receives this broadcast' },
  schedule:   { label: 'Schedule & Review',  desc: 'Set timing and review before creating' },
};

const CreateBroadcast = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const broadcast = location.state?.broadcast || null;
  const isEditMode = !!broadcast;

  const businessAccountId = localStorage.getItem('businessAccountId');
  const phoneNumberId = localStorage.getItem('phoneNumberId');

  const { approvedTemplates, templatesLoading } = useSelector(state => state.broadcasts);
  const { lists: broadcastLists } = useSelector(state => state.broadcastLists);
  const pageRef = useRef(null);
  const errorBannerRef = useRef(null);
  const customerRequestIdRef = useRef(0);

  const [formData, setFormData] = useState({
    name: '',
    templateId: null,
    templateVariables: {},
    scheduleType: 'now',
    scheduledDate: '',
    scheduledTime: ''
  });
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipientMode, setRecipientMode] = useState('list'); // 'list' | 'customers'
  const [selectedListId, setSelectedListId] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
  const [debouncedRecipientSearch, setDebouncedRecipientSearch] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [allCustomers, setAllCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerPagination, setCustomerPagination] = useState({ page: 1, limit: CUSTOMER_FETCH_LIMIT, total: 0, pages: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  // Derived template variables from selected template
  const templateVars = useMemo(() => deriveTemplateVariables(selectedTemplate), [selectedTemplate]);

  // Wizard: visible step ids (skip variables if no vars in selected template)
  const visibleStepIds = useMemo(
    () => ALL_STEP_IDS.filter(id => id !== 'variables' || (selectedTemplate && templateVars.length > 0)),
    [selectedTemplate, templateVars]
  );
  const currentStepId = visibleStepIds[currentStep] ?? 'name';
  const isLastStep = currentStep === visibleStepIds.length - 1;
  const totalSteps = visibleStepIds.length;

  // Effective recipients for individual selection mode
  const effectiveRecipients = useMemo(() => {
    return (allCustomers || []).map(c => ({
      id: c._id || c.id,
      prefix: (c?.prefix || c?.honorificPrefix || '').trim(),
      name: c.name || 'Unnamed contact',
      phone: c.phoneNumber || c.phone
    }));
  }, [allCustomers]);

  const filteredTemplates = approvedTemplates.filter(t => (t.displayName || t.name || '').toLowerCase().includes(templateSearch.toLowerCase()));
  const filteredRecipients = effectiveRecipients;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedRecipientSearch(recipientSearchQuery.trim());
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [recipientSearchQuery]);

  const loadCustomersPage = async ({ page, append = false, search = '' }) => {
    const requestId = ++customerRequestIdRef.current;
    setCustomersLoading(true);
    try {
      const requestParams = {
        page,
        limit: customerPagination.limit || CUSTOMER_FETCH_LIMIT
      };

      if (search) {
        requestParams.search = search;
      }

      const payload = await dispatch(fetchCustomers(requestParams)).unwrap();
      if (requestId !== customerRequestIdRef.current) return;

      const fetchedCustomers = extractCustomersFromPayload(payload);
      const pagination = extractPaginationFromPayload(payload);

      setCustomerPagination((prev) => ({
        page: pagination.page ?? page,
        limit: pagination.limit ?? prev.limit ?? CUSTOMER_FETCH_LIMIT,
        total: pagination.total ?? prev.total ?? fetchedCustomers.length,
        pages: pagination.pages ?? prev.pages ?? 1
      }));

      setAllCustomers((prev) => {
        if (!append) return fetchedCustomers;

        const seen = new Set(prev.map((c) => c._id || c.id));
        const uniqueNew = fetchedCustomers.filter((c) => {
          const id = c._id || c.id;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });

        return [...prev, ...uniqueNew];
      });
    } catch (error) {
      if (requestId !== customerRequestIdRef.current) return;
      // Keep UX resilient even if the next page fails.
      console.error('Failed to load customers page', error);
    } finally {
      if (requestId === customerRequestIdRef.current) {
        setCustomersLoading(false);
      }
    }
  };

  // Load approved templates and broadcast lists on mount
  useEffect(() => {
    dispatch(fetchApprovedTemplates());
    dispatch(fetchBroadcastLists());
  }, [dispatch]);

  useEffect(() => {
    if (recipientMode !== 'customers') return;

    loadCustomersPage({
      page: 1,
      append: false,
      search: debouncedRecipientSearch
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRecipientSearch, recipientMode]);

  // Populate form when editing
  useEffect(() => {
    if (broadcast) {
      const tmplId = broadcast.templateId?._id || broadcast.templateId || null;
      const savedRecipients = (broadcast.recipients || []).map(normalizeRecipient).filter(Boolean);
      setFormData({
        name: broadcast.name || '',
        templateId: tmplId,
        templateVariables: (broadcast.templateParameters || []).reduce((acc, p) => {
          acc[p.variableName] = p.value;
          return acc;
        }, {}),
        scheduleType: broadcast.scheduledFor ? 'scheduled' : 'now',
        scheduledDate: broadcast.scheduledFor ? new Date(broadcast.scheduledFor).toISOString().split('T')[0] : '',
        scheduledTime: broadcast.scheduledFor ? new Date(broadcast.scheduledFor).toTimeString().slice(0, 5) : ''
      });
      const tmpl = approvedTemplates.find(t => t._id === tmplId);
      setSelectedTemplate(tmpl || null);
      if (savedRecipients.length > 0) {
        // Match saved phone numbers back to customer IDs so checkboxes work correctly
        const savedPhones = new Set(savedRecipients.map((recipient) => recipient.phoneNumber));
        const matchedIds = (allCustomers || [])
          .filter(c => savedPhones.has(c.phoneNumber || c.phone))
          .map(c => c._id || c.id);
        setSelectedRecipients(matchedIds.length > 0 ? matchedIds : savedRecipients.map((recipient) => recipient.phoneNumber));
        setRecipientMode('customers');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broadcast]);

  useEffect(() => {
    if (!broadcast || !formData.templateId || approvedTemplates.length === 0) return;

    const matchedTemplate = approvedTemplates.find((template) => template._id === formData.templateId);
    if (matchedTemplate && matchedTemplate._id !== selectedTemplate?._id) {
      setSelectedTemplate(matchedTemplate);
    }
  }, [approvedTemplates, broadcast, formData.templateId, selectedTemplate]);

  useEffect(() => {
    if (!broadcast || recipientMode !== 'customers' || allCustomers.length === 0) return;

    const savedPhones = new Set(
      (broadcast.recipients || [])
        .map(normalizeRecipient)
        .filter(Boolean)
        .map((recipient) => recipient.phoneNumber)
    );
    if (savedPhones.size === 0) return;

    const matchedIds = allCustomers
      .filter((customer) => savedPhones.has(customer.phoneNumber || customer.phone))
      .map((customer) => customer._id || customer.id)
      .filter(Boolean);

    if (matchedIds.length === 0) return;

    setSelectedRecipients((current) => {
      if (current.length === matchedIds.length && matchedIds.every((id) => current.includes(id))) {
        return current;
      }

      return matchedIds;
    });
  }, [allCustomers, broadcast, recipientMode]);

  const focusFirstError = (newErrors) => {
    const errorKeys = Object.keys(newErrors);
    if (errorKeys.length === 0) return;

    const firstKey = errorKeys[0];

    window.requestAnimationFrame(() => {
      const target = pageRef.current?.querySelector(`[data-error-key="${firstKey}"]`);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = target.matches('input, select, textarea') ? target : target.querySelector('input, select, textarea, button');
        if (focusable && typeof focusable.focus === 'function') {
          focusable.focus();
          return;
        }
      }
      errorBannerRef.current?.focus();
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData(prev => ({ ...prev, templateId: template._id, templateVariables: {} }));
    if (errors.templateId) setErrors(prev => ({ ...prev, templateId: '' }));
  };

  const handleVarChange = (key, value) => {
    setFormData(prev => ({ ...prev, templateVariables: { ...prev.templateVariables, [key]: value } }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const handleRecipientToggle = (id) => {
    setSelectedRecipients(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const handleSelectAll = () => {
    if (selectedRecipients.length === effectiveRecipients.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(effectiveRecipients.map(r => r.id));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Broadcast name is required';
    else if (formData.name.length > 100) newErrors.name = 'Max 100 characters';

    if (!formData.templateId) newErrors.templateId = 'Please select an approved template';

    templateVars.forEach(v => {
      if (!formData.templateVariables[v.key]?.trim()) newErrors[v.key] = `${v.label} is required`;
    });

    if (recipientMode === 'list' && !selectedListId) {
      newErrors.recipientIds = 'Please select a broadcast list';
    } else if (recipientMode === 'customers' && selectedRecipients.length === 0) {
      newErrors.recipientIds = 'Please select at least one recipient';
    }

    if (formData.scheduleType === 'scheduled') {
      if (!formData.scheduledDate) newErrors.scheduledDate = 'Date is required';
      if (!formData.scheduledTime) newErrors.scheduledTime = 'Time is required';
      if (formData.scheduledDate && formData.scheduledTime) {
        if (new Date(`${formData.scheduledDate}T${formData.scheduledTime}`) < new Date()) {
          newErrors.scheduledDateTime = 'Scheduled time must be in the future';
        }
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      focusFirstError(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => navigate('/broadcast');

  // Keep currentStep in bounds if visibleStepIds shrinks (e.g. template with vars → without)
  useEffect(() => {
    if (currentStep >= visibleStepIds.length) {
      setCurrentStep(visibleStepIds.length - 1);
    }
  }, [visibleStepIds.length, currentStep]);

  const isStepComplete = (stepId) => {
    switch (stepId) {
      case 'name':       return !!formData.name.trim() && formData.name.length <= 100;
      case 'template':   return !!formData.templateId;
      case 'variables':  return templateVars.every(v => !!formData.templateVariables[v.key]?.trim());
      case 'recipients': return recipientMode === 'list' ? !!selectedListId : selectedRecipients.length > 0;
      case 'schedule':   return true;
      default:           return false;
    }
  };

  const validateCurrentStep = () => {
    const newErrors = {};
    if (currentStepId === 'name') {
      if (!formData.name.trim()) newErrors.name = 'Broadcast name is required';
      else if (formData.name.length > 100) newErrors.name = 'Max 100 characters';
    }
    if (currentStepId === 'template') {
      if (!formData.templateId) newErrors.templateId = 'Please select an approved template';
    }
    if (currentStepId === 'variables') {
      templateVars.forEach(v => {
        if (!formData.templateVariables[v.key]?.trim()) newErrors[v.key] = `${v.label} is required`;
      });
    }
    if (currentStepId === 'recipients') {
      if (recipientMode === 'list' && !selectedListId) newErrors.recipientIds = 'Please select a broadcast list';
      else if (recipientMode === 'customers' && selectedRecipients.length === 0) newErrors.recipientIds = 'Please select at least one recipient';
    }
    if (currentStepId === 'schedule') {
      if (formData.scheduleType === 'scheduled') {
        if (!formData.scheduledDate) newErrors.scheduledDate = 'Date is required';
        if (!formData.scheduledTime) newErrors.scheduledTime = 'Time is required';
        if (formData.scheduledDate && formData.scheduledTime) {
          if (new Date(`${formData.scheduledDate}T${formData.scheduledTime}`) < new Date())
            newErrors.scheduledDateTime = 'Scheduled time must be in the future';
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep()) setCurrentStep(prev => Math.min(prev + 1, visibleStepIds.length - 1));
  };

  const handleBack = () => {
    setErrors({});
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleStepNav = (idx) => {
    if (idx < currentStep) { setErrors({}); setCurrentStep(idx); }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setHasSubmitted(true);
    if (!validateForm()) return;

    let recipients = [];
    if (recipientMode === 'list') {
      const list = broadcastLists.find(l => l._id === selectedListId);
      recipients = list
        ? list.contacts
            .map((contact) => normalizeRecipient({
              phoneNumber: contact.phoneNumber || contact.phone,
              customerName: contact.customerName || contact.name || null
            }))
            .filter(Boolean)
        : [];
    } else {
      recipients = effectiveRecipients
        .filter(r => selectedRecipients.includes(r.id))
        .map((recipient) => normalizeRecipient({
          phoneNumber: recipient.phone,
          customerName: [recipient.prefix, recipient.name].filter(Boolean).join(' ').trim() || recipient.name || null
        }))
        .filter(Boolean);
    }

    const templateParameters = templateVars.map(v => ({
      variableName: v.key,
      value: formData.templateVariables[v.key] || ''
    }));

    const payload = {
      businessAccountId,
      phoneNumberId,
      name: formData.name.trim(),
      templateId: formData.templateId,
      templateParameters,
      recipients,
      scheduledFor: formData.scheduleType === 'scheduled'
        ? new Date(`${formData.scheduledDate}T${formData.scheduledTime}`).toISOString()
        : null,
      status: formData.scheduleType === 'scheduled' ? 'scheduled' : 'draft'
    };

    setSubmitting(true);
    setSubmitError(null);
    try {
      if (isEditMode) {
        await dispatch(updateBroadcast({ id: broadcast._id || broadcast.id, data: payload })).unwrap();
      } else {
        await dispatch(createBroadcast(payload)).unwrap();
      }
      navigate('/broadcast');
    } catch (err) {
      setSubmitError(err?.message || err?.data?.message || 'Failed to save broadcast');
    } finally {
      setSubmitting(false);
    }
  };

  const getMinDate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const hasMoreCustomerPages = customerPagination.page < customerPagination.pages || effectiveRecipients.length < customerPagination.total;
  const selectedList = broadcastLists.find(l => l._id === selectedListId);
  const totalSelectedContacts = recipientMode === 'list' ? (selectedList?.totalContacts || 0) : selectedRecipients.length;
  const errorMessages = Object.values(errors).filter(Boolean);
  const shownRecipientsCount = filteredRecipients.length;
  const remainingRecipientsCount = Math.max((customerPagination.total || 0) - effectiveRecipients.length, 0);
  const nextRecipientBatchCount = Math.min(customerPagination.limit || CUSTOMER_FETCH_LIMIT, remainingRecipientsCount || (customerPagination.limit || CUSTOMER_FETCH_LIMIT));

  const handleLoadMoreRecipients = () => {
    if (!hasMoreCustomerPages || customersLoading) return;
    loadCustomersPage({
      page: customerPagination.page + 1,
      append: true,
      search: debouncedRecipientSearch
    });
  };

  const scheduleSummary = formData.scheduleType === 'scheduled'
    ? (formData.scheduledDate && formData.scheduledTime ? `${formData.scheduledDate} at ${formData.scheduledTime}` : 'Not set yet')
    : 'Save as draft';

  const recipientsSummary = recipientMode === 'list'
    ? (selectedList ? `${selectedList.name} · ${totalSelectedContacts} contacts` : 'No list selected')
    : `${totalSelectedContacts} contact(s) selected`;

  const previewBodyText = selectedTemplate?.components?.body?.text
    ? buildPreview(selectedTemplate.components.body.text, templateVars.filter(v => v.componentType === 'body'), formData.templateVariables)
    : null;
  const previewHeaderText = selectedTemplate?.components?.header?.text
    ? buildPreview(selectedTemplate.components.header.text, templateVars.filter(v => v.componentType === 'header'), formData.templateVariables)
    : null;
  const previewFooterText = selectedTemplate?.components?.footer?.text || null;

  const getReviewValue = (stepId) => {
    if (stepId === 'name') return formData.name || '—';
    if (stepId === 'template') return selectedTemplate?.displayName || selectedTemplate?.name || '—';
    if (stepId === 'variables') {
      const filled = templateVars.filter(v => formData.templateVariables[v.key]).length;
      return `${filled} of ${templateVars.length} variable${templateVars.length !== 1 ? 's' : ''} filled`;
    }
    if (stepId === 'recipients') return recipientsSummary;
    return '—';
  };

  // ── WhatsApp message preview (reused in right panel) ──
  const MessagePreview = () => (
    <div className="cb-phone-wrap">
      <div className="cb-phone-topbar">
        <div className="cb-phone-avatar">W</div>
        <div className="cb-phone-name">WhatsApp Business</div>
      </div>
      <div className="cb-phone-chat">
        <div className="cb-chat-bubble">
          {!selectedTemplate ? (
            <span className="cb-preview-placeholder">Select a template to preview</span>
          ) : (
            <>
              {previewHeaderText && <div className="cb-bubble-header">{previewHeaderText}</div>}
              <div className="cb-bubble-body">{previewBodyText || <span className="cb-preview-placeholder">No body text</span>}</div>
              {previewFooterText && <div className="cb-bubble-footer">{previewFooterText}</div>}
              {(selectedTemplate.components?.buttons || []).length > 0 && (
                <div className="cb-bubble-buttons">
                  {selectedTemplate.components.buttons.map((btn, i) => (
                    <div key={i} className="cb-bubble-btn">{btn.text}</div>
                  ))}
                </div>
              )}
              <div className="cb-bubble-time">now · ✓</div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div ref={pageRef} className="cb-page">

      {/* ── Topbar ── */}
      <header className="cb-topbar">
        <div className="cb-topbar-left">
          <button type="button" className="cb-back-btn" onClick={handleCancel} aria-label="Back">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
          </button>
          <div className="cb-topbar-title">
            <span className="cb-breadcrumb" onClick={handleCancel}>Broadcasts</span>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="cb-breadcrumb-sep"><polyline points="9 18 15 12 9 6"/></svg>
            <h1>{isEditMode ? 'Edit Broadcast' : 'New Broadcast'}</h1>
          </div>
        </div>
        <div className="cb-topbar-right">
          <button type="button" className="cb-btn-ghost" onClick={handleCancel}>Discard</button>
        </div>
      </header>

      {/* ── Error banner ── */}
      {submitError && (
        <div ref={errorBannerRef} className="cb-error-banner" role="alert" tabIndex="-1">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{submitError}</span>
        </div>
      )}

      {/* ── Wizard ── */}
      <div className="cb-wizard">

        {/* ── Left: step navigator ── */}
        <nav className="cb-wizard-nav" aria-label="Wizard steps">
          <div className="cb-nav-header">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#25d366" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5 19.79 19.79 0 0 1 1.63 4.87 2 2 0 0 1 3.6 2.69h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            <span>{isEditMode ? 'Edit Broadcast' : 'New Broadcast'}</span>
          </div>

          <div className="cb-nav-steps">
            {visibleStepIds.map((stepId, idx) => {
              const meta = STEP_META[stepId];
              const isActive = idx === currentStep;
              const isPast = idx < currentStep;
              const complete = isStepComplete(stepId);
              return (
                <button
                  key={stepId}
                  type="button"
                  className={`cb-step-item${isActive ? ' active' : ''}${isPast && complete ? ' done' : ''}${idx > currentStep ? ' upcoming' : ''}`}
                  onClick={() => handleStepNav(idx)}
                  disabled={idx > currentStep}
                  aria-current={isActive ? 'step' : undefined}
                >
                  {idx < visibleStepIds.length - 1 && <div className="cb-step-connector" />}
                  <div className="cb-step-dot">
                    {isPast && complete
                      ? <svg width="11" height="11" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="2 7 6 11 12 3"/></svg>
                      : <span>{idx + 1}</span>}
                  </div>
                  <div className="cb-step-text">
                    <span className="cb-step-label">{meta.label}</span>
                    <span className="cb-step-desc">{meta.desc}</span>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="cb-nav-footer">
            <div className="cb-progress-bar">
              <div className="cb-progress-fill" style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }} />
            </div>
            <span className="cb-progress-text">Step {currentStep + 1} of {totalSteps}</span>
          </div>
        </nav>

        {/* ── Right: content + footer ── */}
        <div className="cb-wizard-right">

          <div className="cb-wizard-body">
            {/* Scrollable step area */}
            <div className="cb-step-scroll">

              {/* Step heading */}
              <div className="cb-step-heading">
                <div className="cb-step-badge">{currentStep + 1}</div>
                <div>
                  <h2 className="cb-step-title">{STEP_META[currentStepId]?.label}</h2>
                  <p className="cb-step-subtitle">{STEP_META[currentStepId]?.desc}</p>
                </div>
              </div>

              {/* ─── Step: Name ─── */}
              {currentStepId === 'name' && (
                <div className="cb-step-body">
                  <div className="cb-name-block">
                    <input
                      type="text" id="name" name="name"
                      value={formData.name} onChange={handleChange}
                      placeholder="e.g., Summer Sale 2025, Monthly Newsletter"
                      className={errors.name ? 'cb-input-xl error' : 'cb-input-xl'}
                      maxLength={100} autoFocus
                    />
                    <div className="cb-input-footer">
                      {errors.name ? <span className="cb-field-error">{errors.name}</span> : <span />}
                      <span className="cb-char-count">{formData.name.length} / 100</span>
                    </div>
                  </div>
                  <div className="cb-step-tip">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                    This name is for internal reference only — it won't be sent to recipients.
                  </div>
                </div>
              )}

              {/* ─── Step: Template ─── */}
              {currentStepId === 'template' && (
                <div className="cb-step-body">
                  {templatesLoading ? (
                    <div className="cb-loading-row"><span className="cb-spinner sm"/><span>Loading approved templates…</span></div>
                  ) : (
                    <>
                      <div className="cb-search-box">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                        </svg>
                        <input type="text" placeholder="Search templates…" value={templateSearch} onChange={e => setTemplateSearch(e.target.value)} className="cb-search-input" />
                        {templateSearch && (
                          <button type="button" className="cb-search-clear" onClick={() => setTemplateSearch('')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        )}
                      </div>
                      {filteredTemplates.length === 0 ? (
                        <div className="cb-empty-state">
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="3"/>
                            <line x1="9" y1="9" x2="15" y2="9"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="15" x2="12" y2="15"/>
                          </svg>
                          <p>{templateSearch ? `No templates matching "${templateSearch}"` : 'No approved templates found.'}</p>
                          <span>Only APPROVED templates can be used for broadcasting.</span>
                        </div>
                      ) : (
                        <div className="cb-template-list" role="radiogroup" aria-label="Approved templates">
                          {filteredTemplates.map(tmpl => (
                            <label key={tmpl._id} className={`cb-template-row${formData.templateId === tmpl._id ? ' selected' : ''}`} htmlFor={`tmpl-${tmpl._id}`}>
                              <input id={`tmpl-${tmpl._id}`} className="sr-only" type="radio" name="templateId" checked={formData.templateId === tmpl._id} onChange={() => handleTemplateSelect(tmpl)} />
                              <div className="cb-template-radio">
                                {formData.templateId === tmpl._id && <svg width="10" height="10" viewBox="0 0 10 10"><circle cx="5" cy="5" r="4" fill="currentColor"/></svg>}
                              </div>
                              <div className="cb-template-info">
                                <div className="cb-template-name">{tmpl.displayName || tmpl.name}</div>
                                <div className="cb-template-preview">{tmpl.components?.body?.text ? tmpl.components.body.text.substring(0, 100) + (tmpl.components.body.text.length > 100 ? '…' : '') : 'No body text'}</div>
                              </div>
                              <div className="cb-template-badges">
                                <span className={`cb-badge cat-${(tmpl.category || '').toLowerCase()}`}>{tmpl.category}</span>
                                <span className="cb-badge lang">{tmpl.language}</span>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                      {errors.templateId && <span className="cb-field-error mt-8">{errors.templateId}</span>}
                    </>
                  )}
                </div>
              )}

              {/* ─── Step: Variables ─── */}
              {currentStepId === 'variables' && (
                <div className="cb-step-body">
                  <div className="cb-step-tip">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                    These values are inserted into the template and sent to every recipient.
                  </div>
                  <div className="cb-vars-grid">
                    {templateVars.map((v, index) => (
                      <div key={v.key} className="cb-field">
                        <label className="cb-label" htmlFor={`tv-${v.key}`}>
                          {v.label}<span className="cb-var-badge">{v.componentType}</span>
                        </label>
                        <input
                          id={`tv-${v.key}`} type="text"
                          value={formData.templateVariables[v.key] || ''}
                          onChange={e => handleVarChange(v.key, e.target.value)}
                          placeholder={`Enter ${v.label.toLowerCase()}`}
                          className={errors[v.key] ? 'cb-input error' : 'cb-input'}
                          data-template-variable={index}
                        />
                        {errors[v.key] && <span className="cb-field-error">{errors[v.key]}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ─── Step: Recipients ─── */}
              {currentStepId === 'recipients' && (
                <div className="cb-step-body">
                  <div className="cb-rcp-tabs" role="tablist" aria-label="Recipient source">
                    <button
                      role="tab" type="button"
                      aria-selected={recipientMode === 'list'}
                      className={`cb-rcp-tab${recipientMode === 'list' ? ' active' : ''}`}
                      onClick={() => { setRecipientMode('list'); setSelectedRecipients([]); }}
                    >
                      <div className="cb-rcp-tab-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                          <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                        </svg>
                      </div>
                      <div className="cb-rcp-tab-body">
                        <span className="cb-rcp-tab-title">Broadcast List</span>
                        <span className="cb-rcp-tab-sub">Use a saved contact list</span>
                      </div>
                      {recipientMode === 'list' && <span className="cb-rcp-active-dot" />}
                    </button>
                    <button
                      role="tab" type="button"
                      aria-selected={recipientMode === 'customers'}
                      className={`cb-rcp-tab${recipientMode === 'customers' ? ' active' : ''}`}
                      onClick={() => { setRecipientMode('customers'); setSelectedListId(''); }}
                    >
                      <div className="cb-rcp-tab-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                      </div>
                      <div className="cb-rcp-tab-body">
                        <span className="cb-rcp-tab-title">Individual Contacts</span>
                        <span className="cb-rcp-tab-sub">Pick contacts manually</span>
                      </div>
                      {recipientMode === 'customers' && <span className="cb-rcp-active-dot" />}
                    </button>
                  </div>

                  {recipientMode === 'list' && (
                    <div className="cb-list-panel">
                      {broadcastLists.length === 0 ? (
                        <div className="cb-empty-state sm"><p>No broadcast lists found. Create one first.</p></div>
                      ) : (
                        <div className="cb-list-options" role="radiogroup">
                          {broadcastLists.map(list => (
                            <label key={list._id} className={`cb-list-row${selectedListId === list._id ? ' selected' : ''}`} htmlFor={`list-${list._id}`}>
                              <input id={`list-${list._id}`} className="sr-only" type="radio" name="selectedList" checked={selectedListId === list._id} onChange={() => setSelectedListId(list._id)} />
                              <div className="cb-list-radio">{selectedListId === list._id && <svg width="10" height="10" viewBox="0 0 10 10"><circle cx="5" cy="5" r="4" fill="currentColor"/></svg>}</div>
                              <div className="cb-list-info">
                                <span className="cb-list-name">{list.name}</span>
                                {list.description && <span className="cb-list-desc">{list.description}</span>}
                              </div>
                              <span className="cb-list-count">{list.totalContacts} contacts</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {selectedList && (
                        <div className="cb-list-preview-chips">
                          {selectedList.contacts.slice(0, 7).map((c, i) => <span key={i} className="cb-chip">{c.name}</span>)}
                          {selectedList.contacts.length > 7 && <span className="cb-chip more">+{selectedList.contacts.length - 7} more</span>}
                        </div>
                      )}
                    </div>
                  )}

                  {recipientMode === 'customers' && (
                    <div className="cb-contacts-panel">
                      <div className="cb-contacts-toolbar">
                        <div className="cb-search-box" style={{ flex: 1 }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                          <input type="text" placeholder="Search by name or phone…" value={recipientSearchQuery} onChange={e => setRecipientSearchQuery(e.target.value)} className="cb-search-input" />
                          {recipientSearchQuery && (
                            <button type="button" className="cb-search-clear" onClick={() => setRecipientSearchQuery('')}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          )}
                        </div>
                        <button type="button" className="cb-btn-ghost sm" onClick={handleSelectAll}>
                          {selectedRecipients.length === effectiveRecipients.length && effectiveRecipients.length > 0 ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div className="cb-contacts-stats" aria-live="polite">
                        <span className={`cb-cs-pill${selectedRecipients.length > 0 ? ' active' : ''}`}>
                          {selectedRecipients.length > 0 && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                          <strong>{customersLoading ? '…' : selectedRecipients.length}</strong>&nbsp;selected
                        </span>
                        <span className="cb-cs-sep">·</span>
                        <span className="cb-cs-pill"><strong>{customersLoading ? '…' : shownRecipientsCount}</strong>&nbsp;loaded</span>
                        <span className="cb-cs-sep">·</span>
                        <span className="cb-cs-pill"><strong>{customersLoading ? '…' : (customerPagination.total || effectiveRecipients.length)}</strong>&nbsp;total</span>
                      </div>
                      <div className="cb-contacts-list" role="region" aria-busy={customersLoading}>
                        {filteredRecipients.length === 0 ? (
                          <div className="cb-empty-state sm"><p>{customersLoading ? 'Loading contacts…' : `No contacts found${debouncedRecipientSearch ? ' matching your search' : ''}.`}</p></div>
                        ) : filteredRecipients.map(r => (
                          <label key={r.id} className={`cb-contact-row${selectedRecipients.includes(r.id) ? ' selected' : ''}`} htmlFor={`r-${r.id}`}>
                            <input id={`r-${r.id}`} type="checkbox" checked={selectedRecipients.includes(r.id)} onChange={() => handleRecipientToggle(r.id)} className="sr-only" />
                            <div className="cb-contact-check">
                              {selectedRecipients.includes(r.id) && <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="2 6 5 9 10 3"/></svg>}
                            </div>
                            <div className="cb-contact-avatar">{r.name.charAt(0).toUpperCase()}</div>
                            <div className="cb-contact-info">
                              <div className="cb-contact-name">{r?.prefix && <span className="cb-prefix">{r.prefix}</span>}{r.name}</div>
                              <div className="cb-contact-phone">{r.phone}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                      {hasMoreCustomerPages && (
                        <button type="button" className="cb-load-more" onClick={handleLoadMoreRecipients} disabled={customersLoading}>
                          {customersLoading ? <><span className="cb-spinner sm"/>Loading…</> : `Load next ${nextRecipientBatchCount} contacts`}
                        </button>
                      )}
                    </div>
                  )}

                  {errors.recipientIds && <span className="cb-field-error mt-8">{errors.recipientIds}</span>}
                </div>
              )}

              {/* ─── Step: Schedule + Review ─── */}
              {currentStepId === 'schedule' && (
                <div className="cb-step-body">
                  <div className="cb-sch-section">
                    <div className="cb-sch-label">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                      </svg>
                      Delivery Timing
                    </div>
                    <div className="cb-schedule-options">
                      <label className={`cb-schedule-opt${formData.scheduleType === 'now' ? ' selected' : ''}`}>
                        <input type="radio" name="scheduleType" value="now" checked={formData.scheduleType === 'now'} onChange={handleChange} className="sr-only" />
                        <div className="cb-schedule-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                          </svg>
                        </div>
                        <div className="cb-schedule-text">
                          <div className="cb-schedule-title">Save as Draft</div>
                          <div className="cb-schedule-desc">Save now and send manually when ready</div>
                        </div>
                        <div className="cb-schedule-check">{formData.scheduleType === 'now' && <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}</div>
                      </label>
                      <label className={`cb-schedule-opt${formData.scheduleType === 'scheduled' ? ' selected' : ''}`}>
                        <input type="radio" name="scheduleType" value="scheduled" checked={formData.scheduleType === 'scheduled'} onChange={handleChange} className="sr-only" />
                        <div className="cb-schedule-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                          </svg>
                        </div>
                        <div className="cb-schedule-text">
                          <div className="cb-schedule-title">Schedule for Later</div>
                          <div className="cb-schedule-desc">Automatically sent at the specified time</div>
                        </div>
                        <div className="cb-schedule-check">{formData.scheduleType === 'scheduled' && <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}</div>
                      </label>
                    </div>

                    {formData.scheduleType === 'scheduled' && (
                      <div className="cb-datetime-row">
                        <div className="cb-field">
                          <label className="cb-label" htmlFor="scheduledDate">Date *</label>
                          <input type="date" id="scheduledDate" name="scheduledDate" value={formData.scheduledDate} onChange={handleChange} min={getMinDate()} className={errors.scheduledDate || errors.scheduledDateTime ? 'cb-input error' : 'cb-input'} />
                          {errors.scheduledDate && <span className="cb-field-error">{errors.scheduledDate}</span>}
                        </div>
                        <div className="cb-field">
                          <label className="cb-label" htmlFor="scheduledTime">Time *</label>
                          <input type="time" id="scheduledTime" name="scheduledTime" value={formData.scheduledTime} onChange={handleChange} className={errors.scheduledTime ? 'cb-input error' : 'cb-input'} />
                          {errors.scheduledTime && <span className="cb-field-error">{errors.scheduledTime}</span>}
                        </div>
                        {errors.scheduledDateTime && <span className="cb-field-error" style={{ gridColumn: '1/-1' }}>{errors.scheduledDateTime}</span>}
                      </div>
                    )}
                  </div>

                  <div className="cb-sch-section">
                    <div className="cb-sch-label">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                      </svg>
                      Review Summary
                    </div>
                    <div className="cb-review-card">
                      <div className="cb-review-rows">
                        {visibleStepIds.filter(id => id !== 'schedule').map(stepId => (
                          <div key={stepId} className="cb-review-row">
                            <span className="cb-review-key">{STEP_META[stepId].label}</span>
                            <span className="cb-review-val">{getReviewValue(stepId)}</span>
                            <button type="button" className="cb-review-edit" onClick={() => { setErrors({}); setCurrentStep(visibleStepIds.indexOf(stepId)); }}>Edit</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* ── Right: live preview panel ── */}
            <div className="cb-preview-panel">
              <div className="cb-preview-label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Live Preview
              </div>
              <MessagePreview />
              {selectedTemplate && (
                <div className="cb-preview-name">{selectedTemplate.displayName || selectedTemplate.name}</div>
              )}
              {currentStepId === 'schedule' && (
                <div className="cb-preview-summary">
                  <div className="cb-ps-row"><span>Recipients</span><strong>{totalSelectedContacts > 0 ? `${totalSelectedContacts} contacts` : '—'}</strong></div>
                  <div className="cb-ps-row"><span>Schedule</span><strong>{scheduleSummary}</strong></div>
                </div>
              )}
            </div>

          </div>

          {/* ── Sticky footer: Back · dots · Next/Submit ── */}
          <div className="cb-wizard-footer">
            <button type="button" className="cb-btn-ghost" onClick={handleBack} disabled={currentStep === 0}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
              Back
            </button>

            <div className="cb-footer-dots">
              {visibleStepIds.map((_, idx) => (
                <span key={idx} className={`cb-dot${idx === currentStep ? ' active' : idx < currentStep ? ' done' : ''}`} />
              ))}
            </div>

            {!isLastStep ? (
              <button type="button" className="cb-btn-primary" onClick={handleNext}>
                Continue
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
            ) : (
              <button type="button" className="cb-btn-primary" onClick={handleSubmit} disabled={submitting}>
                {submitting
                  ? <><span className="cb-spinner" />{isEditMode ? 'Updating…' : 'Creating…'}</>
                  : (isEditMode ? 'Update Broadcast' : 'Create Broadcast')}
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default CreateBroadcast;
